import { graders } from "../../locales/graders";

// --- Constraint converters ---

export function constraintToMeasurment(c: IMeasurmentContraint): IMeasurment {
	return ({
		id: c.id,
		unit: c.unit,
		value: c.default,
		min: c.min || 0,
		max: c.max || 0,
		options: c.options,
		amaMin: c.amaMin || 0,
		amaMax: c.amaMax || 0,
		price: c.price || 0,
		basePrice: c.default * c.price,
		default: c.default,
		miniDescription: c.miniDescription,
		productInfo: c.productInfo,
		productImage: c.productImage || "",
		infoFunction: c.infoFunction || "",
		grader: c.grader || 0,
		bockvinkel: c.bockvinkel || 0,
		platslagarsmyg: c.platslagarsmyg || 0
	})
}

export function constraintToAngles(c: IMeasurmentContraint): IMeasurment {
	let platslagarsmyg = 0;
	graders.map((grader) => {
		if (grader.grader === (c.value || c.default).toString()) {
			platslagarsmyg = Number(grader.platslagarsmyg || 0);
		}
		return grader;
	});
	return ({
		id: c.id,
		unit: c.unit,
		value: c.value || c.default,
		min: c.min || 0,
		max: c.max || 0,
		options: c.options,
		amaMin: c.amaMin || 0,
		amaMax: c.amaMax || 0,
		price: c.price || 0,
		basePrice: c.default * c.price,
		default: c.default,
		miniDescription: c.miniDescription,
		productInfo: c.productInfo,
		productImage: c.productImage || "",
		infoFunction: c.infoFunction || "",
		grader: c.grader || 0,
		bockvinkel: c.bockvinkel || (180 - Number((c.value || c.default) || 0)) || 0,
		platslagarsmyg: c.platslagarsmyg || platslagarsmyg || 0
	})
}

export function constraintToHoles(c: IHole) {
	return ({
		selected: c.selected || false,
		id: c.id,
		shortDescription: c.shortDescription,
		price: c.price,
		value: c.value,
		unit: c.unit || "mm",
		min: c.min || 0,
		max: c.max || 0,
		amaMin: c.amaMin || 0,
		amaMax: c.amaMax || 0,
		image: c.image,
		default: c.selected,
		description: c.description,
		miniDescription: c.miniDescription,
		productInfo: c.productInfo,
		productImage: c.productImage,
		category: c.category,
		infoFunction: c.infoFunction || "",
		measurmentA: c.measurmentA,
		measurmentB: c.measurmentB,
		productionTime: c.productionTime || 0,
	})
}

export function constraintToBakkappa(c: IBakkappa) {
	if (!c) {
		return;
	}
	return {
		selected: c.selected || false,
		id: c.id,
		value: c.value,
		min: c.min,
		max: c.max,
		unit: c.unit || "mm",
		price: c.price,
		image: c.image,
		default: c.default,
		options: c.options,
		description: c.description,
		miniDescription: c.miniDescription,
		productInfo: c.productInfo,
		productImage: c.productImage,
		infoFunction: c.infoFunction || "",
		amaMin: c.amaMin || 0,
		amaMax: c.amaMax || 0,
		vinkel: {
			min: c.vinkel && c.vinkel.min,
			max: c.vinkel && c.vinkel.max,
			amaMin: c.vinkel && c.vinkel.amaMin || 0,
			amaMax: c.vinkel && c.vinkel.amaMax || 0,
			value: c.vinkel && c.vinkel.value,
			default: c.vinkel && c.vinkel.default,
			unit: "",
		},
		productionTime: c.productionTime || 0,
	};
}

export function constraintToGables(c: IGable) {
	if (!c) {
		return;
	}
	return ({
		description: c.description,
		id: c.id,
		image: c.image,
		max: c.max,
		min: c.min,
		amaMax: c.amaMax,
		amaMin: c.amaMin,
		category: c.category,
		options: c.options,
		postfix: c.postfix,
		price: c.price,
		selected: c.selected || false,
		shortDescription: c.shortDescription,
		unit: c.unit,
		value: c.value || c.default,
		default: c.default,
		basePrice: c.price,
		groupId: c.groupId,
		miniDescription: c.miniDescription || c.minDescription,
		measurmentA: c.measurmentA,
		measurmentB: c.measurmentB,
		funcName: c.funcName,
		productInfo: c.productInfo,
		productImage: c.productImage,
		infoFunction: c.infoFunction || "",
		productionTime: c.productionTime || 0,
	})
}

// --- Product creation ---

/**
 * Creates a product from a template with given material.
 * Pass `fallbackMaterials` (state.materials) for cases where template has no embedded material.
 */
export function createProduct(
	template: ISheetProductTemplate,
	material: ISheetMaterial | undefined,
	fallbackMaterials: ISheetMaterial[]
): ISheetProduct {
	if (template && template.material && Array.isArray(template.material) && template.material.length > 0) {
		material = template.material[0] as ISheetMaterial;
		material.selectedThickness = material.thickness && material.thickness.length && material.thickness[0].value || 0;
		material.selectedThicknessPrice = material.thickness && material.thickness.length && material.thickness[0].price || 0;
	} else if (!material) {
		material = fallbackMaterials[0] as ISheetMaterial;
		material.selectedThickness = material.thickness && material.thickness.length && material.thickness[0].value || 0;
		material.selectedThicknessPrice = material.thickness && material.thickness.length && material.thickness[0].price || 0;
	}

	const category = (function () {
		const parts = template.category.split("/")
		return parts[parts.length - 1]
	})()

	const product: ISheetProduct = {
		name: template.name,
		images: template.images,
		template: template._id,
		userComment: "",
		userUploads: [[], [], []],
		measurements: template.measurements.map(constraintToMeasurment),
		angles: template.angles.map(constraintToAngles),
		gables: template.gables.map(constraintToGables),
		// @ts-expect-error — interface mismatch
		bakkappa: template && template.bakkappa.map(constraintToBakkappa) || [],
		// @ts-expect-error — interface mismatch
		skorstenstopp: template && template.skorstenstopp.map(constraintToBakkappa) || [],
		// @ts-expect-error — interface mismatch
		skorstensranna: template && template.skorstensranna.map(constraintToBakkappa) || [],
		// @ts-expect-error — interface mismatch
		underbeslag: template && template.underbeslag && template.underbeslag.map(constraintToBakkappa) || [],
		holes: template.holes ? template.holes.map(constraintToHoles) : template.holes,
		fastblack: template && template.fastblack || undefined,
		skarvHakfals: template['skarv-hakfals'] && template['skarv-hakfals'].map(constraintToGables) || [],
		slussSkarvs: template['sluss-skarv'] && template['sluss-skarv'].map(constraintToGables) || [],
		skarvOverlapps: template['skarv-overlapp'] && template['skarv-overlapp'].map(constraintToGables) || [],
		dubbelTvarfals: template['dubbel-tvarfals'] && template['dubbel-tvarfals'].map(constraintToGables) || [],
		dubbelStandfals: template['dubbel-standfals'] && template['dubbel-standfals'].map(constraintToGables) || [],
		enkelSvangdFals: template['enkel-svangd-fals'] && template['enkel-svangd-fals'].map(constraintToGables) || [],
		enkelStandfals: template['enkel-standfals'] && template['enkel-standfals'].map(constraintToGables) || [],
		// @ts-expect-error — interface mismatch
		"funktioner-fotplat-skarv-hakfals": template['funktioner-fotplat-skarv-hakfals'] && template['funktioner-fotplat-skarv-hakfals'].map(constraintToGables) || [],
		// @ts-expect-error — interface mismatch
		"funktioner-fotranna-dubbel-tvarfals": template['funktioner-fotranna-dubbel-tvarfals'] && template['funktioner-fotranna-dubbel-tvarfals'].map(constraintToGables) || [],
		// @ts-expect-error — interface mismatch
		"funktioner-fotplat-dubbel-tvarfals": template['funktioner-fotplat-dubbel-tvarfals'] && template['funktioner-fotplat-dubbel-tvarfals'].map(constraintToGables) || [],
		// @ts-expect-error — interface mismatch
		"funktioner-skyddsplat-skarv-overlapp": template['funktioner-skyddsplat-skarv-overlapp'] && template['funktioner-skyddsplat-skarv-overlapp'].map(constraintToGables) || [],
		// @ts-expect-error — interface mismatch
		"funktioner-stalplat-dubbel-standfals": template['funktioner-stalplat-dubbel-standfals'] && template['funktioner-stalplat-dubbel-standfals'].map(constraintToGables) || [],
		material: { ...material },
		clipsToMeasure: template.clipsToMeasure,
		linearMeter: template.linearMeter,
		glg: template.glg,
		selectableAccessories: template.selectableAccessories,
		infastningproducts: template.infastningproducts,
		ovrigtProducts: template.ovrigtProducts,
		category: category,
		isOwnProduct: template.isOwnProduct,
		basePrice: template.basePrice,
		materialType: template.materialType,
		additionalPrice: {
			measurements: 0,
			angles: 0,
			fastblack: 0,
			selectableAccessories: 0,
			holes: 0,
			bakkappa: 0,
			gables: 0
		},
		showSqm: template.showSqm,
		showTillklipp: template.showTillklipp
	}
	return product;
}

// --- Category helpers ---

export function buildCategoryListFromTemplateCategory(category: string, categories: ICategory[]): ICategory[] {
	const result: ICategory[] = []
	const parts = category.split("/");
	let lookup = categories[0].contents;
	for (const part of parts) {
		const partial = lookup && lookup.find(i => i.name === part);
		if (partial == undefined) {
			return result
		}
		result.push(partial as ICategory)
		lookup = (partial as ICategory).contents
	}
	return result
}
